// frontend/app/components/lessonPlans/present/PresentChrome.js
//
// Where you are, and the four controls a teacher without a clicker needs.
//
// "Hide things that aren't used, kinda like PowerPoint present" is the whole
// brief for this file. The workspace's header carries a breadcrumb, sibling
// lesson chips, duration and difficulty pills, a progress count and an Insights
// button; none of that is operable in front of a class and all of it is gone.
// What remains is a phase name, a step counter, and controls — at chrome size,
// which is the one size in the scale that does not grow with the viewport,
// because a projector-sized step counter competes with the lesson.

import React from "react";
import { StyleSheet, Text, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getPhaseMeta } from "../lessonPlanTheme";
import { ChromeButton } from "./PresentSurface";

/** Top bar: the lesson's identity on the left, the way out on the right. */
export const PresentTopBar = ({
  lesson,
  step,
  index,
  total,
  colors,
  type,
  notesVisible,
  hasNotes,
  onToggleNotes,
  sarasOpen,
  onToggleSaras,
  presenterOpen,
  onTogglePresenter,
  onExit,
  t,
}) => {
  const translate = t || ((value) => value);
  const phase = getPhaseMeta(step?.phase);

  return (
    <View style={[styles.topBar, { borderBottomColor: colors.border }]}>
      <View style={styles.topLeft}>
        <View style={[styles.phaseDot, { backgroundColor: phase.accent }]} />
        <Text style={[styles.phaseLabel, type.chrome, { color: phase.accent }]}>
          {translate(phase.label)}
        </Text>
        <Text style={[styles.counter, type.chrome, { color: colors.textMuted }]}>
          {translate("Step")} {index + 1} {translate("of")} {total}
        </Text>
      </View>

      <Text
        style={[styles.lessonTitle, type.chrome, { color: colors.textMuted }]}
        numberOfLines={1}
      >
        {lesson?.title}
      </Text>

      <View style={styles.topRight}>
        {/* The toggle is a quiet outline while notes are off and a warm filled
            chip while they are on, so the teacher can tell at a glance what the
            room can currently read without the control itself being loud. */}
        {hasNotes ? (
          <ChromeButton
            colors={colors}
            type={type}
            icon={notesVisible ? "eye-outline" : "note-text-outline"}
            label={notesVisible ? translate("Notes visible") : translate("Notes")}
            active={notesVisible}
            onPress={onToggleNotes}
          />
        ) : null}

        {/* The second of a matching pair: same shape, same corner, same
            press-to-open-press-to-close gesture as the notes toggle. It takes
            an accent so the two fills never read as the same state — notes
            open is warm, Saras open is Saras blue — which is exactly the
            distinction ChromeButton's `accent` exists to make. */}
        {onToggleSaras ? (
          <ChromeButton
            colors={colors}
            type={type}
            icon="robot-happy-outline"
            label={sarasOpen ? translate("Saras open") : translate("Ask Saras")}
            active={sarasOpen}
            accent={sarasOpen ? colors.accentPrimary : undefined}
            onPress={onToggleSaras}
          />
        ) : null}

        {/* The third of the pair-turned-trio, and the one that changes what the
            other two mean: with a presenter window open the teacher's notes are
            no longer something the room can read, so N stops being a decision
            about privacy. Green rather than warm or Saras blue, because "the
            notes are somewhere else now" is a different fact from either of the
            states those two colours already stand for. */}
        {onTogglePresenter ? (
          <ChromeButton
            colors={colors}
            type={type}
            icon="monitor-multiple"
            label={
              presenterOpen ? translate("Presenter open") : translate("Presenter view")
            }
            active={presenterOpen}
            accent={presenterOpen ? colors.success : undefined}
            onPress={onTogglePresenter}
          />
        ) : null}

        <ChromeButton
          colors={colors}
          type={type}
          icon="close"
          label={translate("Exit")}
          onPress={onExit}
        />
      </View>
    </View>
  );
};

/**
 * A one-line strip for something the teacher has to be told mid-lesson.
 *
 * There is exactly one of these so far: the browser refused the presenter
 * window. That refusal is silent by design — `window.open` returns null and
 * throws nothing — so without this the teacher presses a button, nothing
 * happens, and they press it again in front of the class. The text has to name
 * the fix, not the fault, and the strip has to be dismissible: a warning that
 * cannot be put away is a warning the class reads for the rest of the lesson.
 */
export const PresentNotice = ({ colors, type, message, actionLabel, onDismiss }) => (
  <View
    style={[
      styles.notice,
      { backgroundColor: colors.teacherTipBg, borderColor: colors.teacherTipBorder },
    ]}
  >
    <MaterialCommunityIcons
      name="alert-outline"
      size={16}
      color={colors.teacherTipText}
    />
    <Text
      style={[styles.noticeText, type.chrome, { color: colors.teacherTipText }]}
    >
      {message}
    </Text>
    {onDismiss ? (
      <ChromeButton
        colors={colors}
        type={type}
        icon="close"
        label={actionLabel}
        onPress={onDismiss}
      />
    ) : null}
  </View>
);

/**
 * Bottom bar: step position as a bar rather than a number, and the same three
 * moves the clicker makes. The primary control is whatever Space would do next,
 * so the button and the remote never disagree about what happens.
 */
export const PresentBottomBar = ({
  index,
  total,
  colors,
  type,
  revealsLeft,
  onPrevious,
  onNext,
  onAdvance,
  t,
}) => {
  const translate = t || ((value) => value);
  const pct = total > 0 ? Math.round(((index + 1) / total) * 100) : 0;
  const atEnd = index >= total - 1;

  return (
    <View style={styles.bottomWrap}>
      <View style={[styles.progressTrack, { backgroundColor: colors.border }]}>
        <View
          style={[
            styles.progressFill,
            { backgroundColor: colors.accentPrimary, width: `${pct}%` },
          ]}
        />
      </View>

      <View style={styles.bottomBar}>
        <ChromeButton
          colors={colors}
          type={type}
          icon="chevron-left"
          label={translate("Back")}
          disabled={index <= 0}
          onPress={onPrevious}
        />

        <View style={styles.bottomSpacer} />

        {revealsLeft > 0 ? (
          <ChromeButton
            colors={colors}
            type={type}
            icon="eye-outline"
            label={
              revealsLeft > 1
                ? `${translate("Reveal")} (${revealsLeft})`
                : translate("Reveal")
            }
            active
            accent={colors.accentPrimary}
            onPress={onAdvance}
          />
        ) : null}

        <ChromeButton
          colors={colors}
          type={type}
          icon="chevron-right"
          label={atEnd ? translate("End of lesson") : translate("Next")}
          disabled={atEnd}
          onPress={onNext}
        />
      </View>
    </View>
  );
};

/**
 * A one-line reminder of the keys, for a teacher meeting the mode cold.
 *
 * What Escape does changes with the Saras panel, so what the hint says changes
 * with it too. A line reading "Esc to exit" while Escape is actually closing
 * the panel teaches the teacher to distrust the hint, and the one press they
 * then avoid is the one that gets them out.
 */
export const PresentKeyHint = ({
  colors,
  type,
  sarasOpen = false,
  // P is only mentioned where a second window is actually possible. A key that
  // is advertised and does nothing is worse than one nobody knew about.
  presenterAvailable = false,
  t,
}) => {
  const translate = t || ((value) => value);
  return (
    <View style={styles.hintRow}>
      <MaterialCommunityIcons name="keyboard-outline" size={13} color={colors.textMuted} />
      <Text style={[styles.hintText, type.chrome, { color: colors.textMuted }]}>
        {sarasOpen
          ? translate("← → or a clicker to move · Esc or S to close Saras")
          : presenterAvailable
          ? translate(
              "← → or a clicker to move · Space to reveal · N for notes · P for presenter · S for Saras · Esc to exit"
            )
          : translate(
              "← → or a clicker to move · Space to reveal · N for notes · S for Saras · Esc to exit"
            )}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    paddingHorizontal: 24,
    paddingVertical: 12,
    gap: 16,
  },
  topLeft: { flexDirection: "row", alignItems: "center", flexShrink: 0 },
  phaseDot: { width: 9, height: 9, borderRadius: 5, marginRight: 8 },
  phaseLabel: {
    fontWeight: "800",
    letterSpacing: 1.1,
    textTransform: "uppercase",
    marginRight: 14,
  },
  counter: { fontWeight: "600" },
  lessonTitle: { flex: 1, textAlign: "center", fontWeight: "600" },
  topRight: { flexDirection: "row", alignItems: "center", gap: 8, flexShrink: 0 },

  notice: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginHorizontal: 24,
    marginTop: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
  },
  noticeText: { flex: 1, fontWeight: "700", marginLeft: 8 },

  bottomWrap: { width: "100%" },
  progressTrack: { height: 3, width: "100%" },
  progressFill: { height: 3 },
  bottomBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 12,
    gap: 10,
  },
  bottomSpacer: { flex: 1 },

  hintRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: 10,
    gap: 8,
  },
  hintText: { fontWeight: "500", marginLeft: 8 },
});
